
import { Button } from "@/components/ui/button";
import { Plus, CalendarRange, ChevronDown, MoreHorizontal } from "lucide-react";
import { useTranslation } from "@/i18n/useTranslation";
import { format } from "date-fns";
import { Board, Project, Sprint } from "./boardTypes";
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from "@/components/ui/popover";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from "@/components/ui/alert-dialog";
import { useState, useEffect } from "react";
import { parsePlainDateString } from "@/utils/dateTransforms";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface BoardHeaderProps {
  boards: Board[];
  selectedBoardId: string | null;
  setSelectedBoardId: (id: string) => void;
  selectedProject: Project | null;
  selectedSprint: Sprint | null;
  openBoardSelector: boolean;
  setOpenBoardSelector: (open: boolean) => void;
  onCreateTask: () => void;
  onCreateSprint: () => void;
  onDeleteSprint?: (sprintId: string, options: { deleteTasks: boolean, targetSprintId?: string }) => void;
  getAllSprints?: () => Promise<Sprint[]>;
}

export const BoardHeader = ({
  boards,
  selectedBoardId,
  setSelectedBoardId,
  selectedProject,
  selectedSprint,
  openBoardSelector,
  setOpenBoardSelector,
  onCreateTask,
  onCreateSprint,
  onDeleteSprint,
  getAllSprints
}: BoardHeaderProps) => {
  const { t } = useTranslation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTasks, setDeleteTasks] = useState(false);
  const [availableSprints, setAvailableSprints] = useState<Sprint[]>([]);
  const [targetSprintId, setTargetSprintId] = useState<string>('');
  
  // Fetch all sprints when the dialog opens
  useEffect(() => {
    if (deleteDialogOpen && getAllSprints && selectedSprint) {
      getAllSprints().then(sprints => {
        // Filter out the current sprint
        const filteredSprints = sprints.filter(sprint => sprint.id !== selectedSprint.id);
        setAvailableSprints(filteredSprints);
        
        // Pre-select the first available sprint if any
        if (filteredSprints.length > 0) {
          setTargetSprintId(filteredSprints[0].id);
        }
      });
    }
  }, [deleteDialogOpen, getAllSprints, selectedSprint]);
  
  const handleDeleteSprint = () => {
    if (selectedSprint && onDeleteSprint) {
      onDeleteSprint(
        selectedSprint.id, 
        { 
          deleteTasks, 
          targetSprintId: !deleteTasks && targetSprintId ? targetSprintId : undefined 
        }
      );
    }
    setDeleteDialogOpen(false);
  };
  
  const handleOpenDeleteDialog = () => {
    // Reset the state when opening the dialog
    setDeleteTasks(false);
    setTargetSprintId('');
    setDeleteDialogOpen(true);
  };
  
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-4">
          {boards.length > 0 && (
            <Popover open={openBoardSelector} onOpenChange={setOpenBoardSelector}>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1 font-semibold text-xl hover:text-primary transition-colors">
                  {selectedProject?.name || t('board.boardTitle')}
                  <ChevronDown className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-0">
                <div className="py-1">
                  {boards.map((board) => (
                    <button 
                      key={board.id} 
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground ${board.id === selectedBoardId ? 'bg-accent/50' : ''}`}
                      onClick={() => {
                        setSelectedBoardId(board.id);
                        setOpenBoardSelector(false);
                      }}
                    >
                      {board.project_name || t('board.boardTitle')}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
        
        <div className="flex-1 flex justify-center relative">
          {selectedSprint ? (
            <div className="text-center flex items-center gap-3 justify-center">
              <h1 className="text-2xl font-bold">
                {selectedSprint.title}
              </h1>
              <div className="flex items-center text-muted-foreground">
                <CalendarRange className="h-4 w-4 mr-1" />
                <span>
                  {format(parsePlainDateString(selectedSprint.start_date)!, 'MMM d')} - {format(parsePlainDateString(selectedSprint.end_date)!, 'MMM d, yyyy')}
                </span>
              </div>
              
              {/* Sprint Actions Dropdown */}
              {selectedSprint && selectedSprint.status !== 'backlog' && (
                <div className="ml-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem 
                        onClick={handleOpenDeleteDialog}
                        className="text-destructive focus:text-destructive"
                      >
                        {t('board.deleteSprint')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          ) : (
            <h1 className="text-2xl font-bold text-center">
              {t('board.allSprints')}
            </h1>
          )}
        </div>
          
        <div className="flex space-x-2">
          <Button onClick={onCreateTask}>
            <Plus className="h-4 w-4 mr-2" />
            {t('board.addTask')}
          </Button>
        </div>
      </div>
      
      {/* Delete Sprint Confirmation Dialog with task options */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { setTimeout(() => { document.body.style.pointerEvents = ""; setDeleteDialogOpen(open); }, 100) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('board.deleteSprintConfirmation')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('board.deleteSprintWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="flex items-center space-x-2">
              <Switch id="delete-tasks" checked={deleteTasks} onCheckedChange={setDeleteTasks} />
              <Label htmlFor="delete-tasks">{t('board.deleteAllTasks')}</Label>
            </div>
            
            {!deleteTasks && availableSprints.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="target-sprint">{t('board.moveTasksTo')}</Label>
                <Select 
                  value={targetSprintId} 
                  onValueChange={setTargetSprintId}
                  required
                >
                  <SelectTrigger id="target-sprint" className="w-full">
                    <SelectValue placeholder={t('board.selectSprint')} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSprints.map((sprint) => (
                      <SelectItem key={sprint.id} value={sprint.id}>
                        {sprint.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {!deleteTasks && availableSprints.length === 0 && (
              <div className="text-amber-500 text-sm">
                {t('board.noSprintsAvailable')}
              </div>
            )}
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSprint} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={!deleteTasks && (!targetSprintId || availableSprints.length === 0)}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
